import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { 
  HotKey, 
  Category, 
  Station, 
  User, 
  LoginCredentials,
  MessageType
} from '../types';
import { resolveMessageImageUrl } from '../utils/resolveMessageImageUrl';
import { DEFAULT_HOTQUU_TILE_COLOR } from '../constants/hotQuuTheme';

/** Avoid `Authorization: Bearer Bearer …` if storage included a `Bearer ` prefix. */
export function normalizeQuuBearerToken(raw: string): string {
  let s = String(raw).trim();
  while (/^bearer\s+/i.test(s)) {
    s = s.replace(/^bearer\s+/i, '').trim();
  }
  return s;
}

export type AuthTokenExpiryInfo = {
  issuedAt: Date | null;
  expiresAt: Date;
  lifetimeMinutes: number | null;
  remainingMinutes: number;
  raw: { iat?: number; exp: number };
};

/** Decode JWT payload (no signature verify) and read `exp` / `iat`. */
export function getAuthTokenExpiryInfo(rawToken?: string | null): AuthTokenExpiryInfo | null {
  const raw = rawToken ?? (typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null);
  if (!raw) return null;
  try {
    const token = normalizeQuuBearerToken(raw);
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded)) as { iat?: number; exp?: number };
    if (typeof payload.exp !== 'number') return null;
    const expiresAt = new Date(payload.exp * 1000);
    const issuedAt = typeof payload.iat === 'number' ? new Date(payload.iat * 1000) : null;
    const lifetimeMinutes =
      issuedAt != null ? Math.round((expiresAt.getTime() - issuedAt.getTime()) / 60000) : null;
    const remainingMinutes = Math.round((expiresAt.getTime() - Date.now()) / 60000);
    return {
      issuedAt,
      expiresAt,
      lifetimeMinutes,
      remainingMinutes,
      raw: { iat: payload.iat, exp: payload.exp },
    };
  } catch {
    return null;
  }
}

/** Log token lifetime for QA / ops (safe: no token value printed). */
export function logAuthTokenExpiry(context: string, rawToken?: string | null): void {
  const info = getAuthTokenExpiryInfo(rawToken);
  if (!info) {
    console.info(`[HotQuus session] ${context}: no JWT exp claim found`);
    return;
  }
  console.info(
    `[HotQuus session] ${context}: lifetime=${
      info.lifetimeMinutes != null ? `${info.lifetimeMinutes} min` : 'unknown'
    }, expiresAt=${info.expiresAt.toISOString()}, remaining≈${info.remainingMinutes} min`
  );
}

/** Set by apiService on auth expiry; Login page reads and clears it. */
export const SESSION_EXPIRED_MESSAGE_KEY = 'hotquuSessionExpiredMessage';
export const SESSION_EXPIRED_MESSAGE = 'Session expired.';

export function isSessionExpiredError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as { message?: string; status?: number; response?: { status?: number } };
  return (
    e.message === SESSION_EXPIRED_MESSAGE ||
    e.status === 401 ||
    e.response?.status === 401
  );
}

type AuthRetryConfig = InternalAxiosRequestConfig & { _authRetried?: boolean };

class ApiService {
  private api: AxiosInstance;
  /** Authenticated Quu2Go Web API calls: refreshes the JWT and retries once on 401. */
  private quu: AxiosInstance;
  private refreshInFlight: Promise<string> | null = null;
  private static readonly TOKEN_REFRESH_LEEWAY_MS = 60_000;
  private sessionExpiredHandling = false;
  /**
   * Quu2Go Web API base (login, stations, BlastHotQuus).
   * Keep auth + blast APIs on the same host so the login JWT is accepted.
   * Override: REACT_APP_QUU2GO_WEB_API_BASE. Staging is HTTP-only, so from the HTTPS
   * site use the nginx proxy: https://hotquus.quu2go.com/quu2go-staging-api/Quu2GoWebApi
   */
  private quu2GoWebApiBase = (
    process.env.REACT_APP_QUU2GO_WEB_API_BASE?.trim() || 'https://quu2go.com/Quu2GoWebApi'
  ).replace(/\/+$/, '');

  private get loginApiURL(): string {
    return `${this.quu2GoWebApiBase}/api/User/login`;
  }
  private get getUserStationsURL(): string {
    return `${this.quu2GoWebApiBase}/api/AppCommon/GetUserStations`;
  }
  private get getNowPlayingURL(): string {
    return `${this.quu2GoWebApiBase}/api/Dashboard/GetNowPlaying`;
  }
  private get getUserDetailsURL(): string {
    return `${this.quu2GoWebApiBase}/api/User/GetUserDetails`;
  }
  private get refreshTokenURL(): string {
    return `${this.quu2GoWebApiBase}/api/User/RefreshToken`;
  }
  private get validateTokenURL(): string {
    return `${this.quu2GoWebApiBase}/api/User/ValidateToken`;
  }
  /** Quu2Go Web API — create/update HotQuu (multipart/form-data). */
  private get blastHotQuusSaveURL(): string {
    return `${this.quu2GoWebApiBase}/api/BlastHotQuus/Save`;
  }
  /** Quu2Go Web API — list HotQuus for a station. */
  private get blastHotQuusListURL(): string {
    return `${this.quu2GoWebApiBase}/api/BlastHotQuus/List`;
  }
  /** Quu2Go Web API — activate / deactivate HotQuu. */
  private get blastHotQuusUpdateURL(): string {
    return `${this.quu2GoWebApiBase}/api/BlastHotQuus/Update`;
  }
  /** HotQuus categories — /api/HotQuusCategory/List?stationId={station_id} */
  private get hotQuusCategoryListURL(): string {
    return `${this.quu2GoWebApiBase}/api/HotQuusCategory/List`;
  }
  /** BlastHotQuus Save/Update: Type is always 2 (HotQuu blast type). */
  private static readonly BLAST_HOTQUU_TYPE = 2;

  /**
   * Our HotQuus API (image upload, etc.).
   * - Override: REACT_APP_API_URL
   * - On HTTPS pages: same-origin `/api` (nginx → local upload service)
   * - Otherwise: local dev default
   */
  private resolveApiBase(): string {
    const fromEnv = process.env.REACT_APP_API_URL?.trim();
    if (fromEnv) return fromEnv.replace(/\/+$/, '');

    if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
      return `${window.location.origin}/api`;
    }
    return 'http://localhost:3001/api';
  }

  private get baseURL(): string {
    return this.resolveApiBase();
  }

  constructor() {
    this.api = axios.create({
      baseURL: this.baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to include auth token
    this.api.interceptors.request.use((config) => {
      const tokenRaw = localStorage.getItem('authToken');
      if (tokenRaw) {
        config.headers.Authorization = `Bearer ${normalizeQuuBearerToken(tokenRaw)}`;
      }
      // FormData must use multipart boundary from the runtime — drop JSON Content-Type
      if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
        if (config.headers && typeof config.headers.set === 'function') {
          config.headers.set('Content-Type', false as unknown as string);
        } else if (config.headers) {
          delete (config.headers as Record<string, unknown>)['Content-Type'];
          delete (config.headers as Record<string, unknown>)['content-type'];
        }
      }
      return config;
    });

    // Authenticated 401s are handled per-call via handleSessionExpired()
    // (not here — a global redirect broke post-login → select-station).
    this.api.interceptors.response.use(
      (response) => response,
      (error) => Promise.reject(error)
    );

    this.quu = axios.create({ withCredentials: false });

    this.quu.interceptors.request.use(async (config) => {
      let token = this.getStoredToken();
      if (token && this.isTokenExpiringSoon(token)) {
        try {
          token = await this.refreshAuthToken();
        } catch {
          // Send with the current token; the 401 handler below decides what happens next.
        }
      }
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Callers still run ensureAuthorized(): a 401 that reaches them means refresh failed.
    this.quu.interceptors.response.use(
      (response) => response,
      async (error) => {
        const config = error?.config as AuthRetryConfig | undefined;
        if (error?.response?.status !== 401 || !config || config._authRetried) {
          return Promise.reject(error);
        }
        config._authRetried = true;
        try {
          await this.refreshAuthToken();
        } catch {
          return Promise.reject(error);
        }
        return this.quu.request(config);
      }
    );
  }

  private getStoredToken(): string | null {
    const raw = localStorage.getItem('authToken');
    return raw ? normalizeQuuBearerToken(raw) : null;
  }

  private isTokenExpiringSoon(token: string): boolean {
    const info = getAuthTokenExpiryInfo(token);
    if (!info) return false;
    return info.expiresAt.getTime() - Date.now() < ApiService.TOKEN_REFRESH_LEEWAY_MS;
  }

  /**
   * Exchange the stored JWT for a new one via User/RefreshToken (accepts expired tokens).
   * Concurrent callers share one in-flight request.
   */
  async refreshAuthToken(): Promise<string> {
    if (this.refreshInFlight) return this.refreshInFlight;

    const current = this.getStoredToken();
    if (!current) {
      return Promise.reject(new Error('No token to refresh'));
    }

    this.refreshInFlight = (async () => {
      const response = await axios.post(this.refreshTokenURL, null, {
        headers: { accept: '*/*', Authorization: `Bearer ${current}` },
        withCredentials: false,
      });
      const d = response.data ?? {};
      const raw = d.token ?? d.Token ?? d.accessToken ?? d.AccessToken;
      if (!raw || typeof raw !== 'string') {
        throw new Error('RefreshToken returned no token');
      }
      const next = normalizeQuuBearerToken(raw);
      // Don't resurrect a session the user logged out of while the refresh was in flight.
      if (localStorage.getItem('authToken')) {
        localStorage.setItem('authToken', next);
      }
      logAuthTokenExpiry('token refreshed', next);
      return next;
    })().finally(() => {
      this.refreshInFlight = null;
    });

    return this.refreshInFlight;
  }

  /** User/ValidateToken — `{ valid, message }`; a 401 means the token is expired/invalid. */
  async validateAuthToken(): Promise<{ valid: boolean; message?: string }> {
    const token = this.getStoredToken();
    if (!token) return { valid: false, message: 'No token' };
    try {
      const response = await axios.get(this.validateTokenURL, {
        headers: { accept: '*/*', Authorization: `Bearer ${token}` },
        withCredentials: false,
      });
      return { valid: response.data?.valid !== false, message: response.data?.message };
    } catch (error: any) {
      if (error?.response?.status === 401) {
        return { valid: false, message: error.response.data?.message };
      }
      throw error;
    }
  }

  /**
   * On app start: validate the stored token and refresh it if needed.
   * Returns false only when the server rejects both; network errors keep the session.
   */
  async ensureValidSession(): Promise<boolean> {
    try {
      const { valid } = await this.validateAuthToken();
      if (valid) return true;
      await this.refreshAuthToken();
      return true;
    } catch (error: any) {
      return !error?.response;
    }
  }

  /** Attach HTTP status onto a thrown Error so UI can detect 401 without axios. */
  private attachHttpStatus(error: Error, status?: number, response?: unknown): Error {
    if (status != null) (error as Error & { status?: number }).status = status;
    if (response != null) (error as Error & { response?: unknown }).response = response;
    return error;
  }

  /**
   * Clear session and send the user to login with "Session expired."
   * Used when authenticated Quu2Go APIs return 401 (expired/invalid token).
   */
  private handleSessionExpired(): never {
    if (!this.sessionExpiredHandling) {
      this.sessionExpiredHandling = true;
      try {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        localStorage.removeItem('selectedStation');
        localStorage.removeItem('userStations');
        localStorage.removeItem('hotquuSessionStartedAt');
        sessionStorage.setItem(SESSION_EXPIRED_MESSAGE_KEY, SESSION_EXPIRED_MESSAGE);
      } catch {
        /* ignore storage errors */
      }

      const path = (window.location.pathname || '/').replace(/\/$/, '') || '/';
      if (path !== '/login' && !path.endsWith('/login')) {
        window.location.href = '/login';
      }
    }
    throw this.attachHttpStatus(new Error(SESSION_EXPIRED_MESSAGE), 401);
  }

  /** If the error is HTTP 401 from an authenticated call, force session expiry logout. */
  private ensureAuthorized(error: any): void {
    if (error?.response?.status === 401 || error?.status === 401) {
      this.handleSessionExpired();
    }
  }

  /** Resolve station id: explicit override → localStorage selectedStation → default. */
  private resolveStationId(stationIdOverride?: string): string {
    let stationId: string | undefined =
      stationIdOverride != null && stationIdOverride !== '' ? stationIdOverride : undefined;
    if (stationId == null) {
      const selectedStation = localStorage.getItem('selectedStation');
      if (selectedStation) {
        try {
          const station = JSON.parse(selectedStation);
          stationId = station.id != null ? station.id.toString() : undefined;
        } catch {
          // ignore
        }
      }
    }
    if (stationId == null || stationId === '') {
      stationId = '180638';
    }
    return stationId;
  }

  // Authentication
  async login(credentials: LoginCredentials): Promise<{ user: User; token: string }> {
    try {
      console.log('Logging in with real API...');
      const response = await axios.post(this.loginApiURL, {
        username: credentials.username,
        password: credentials.password
      }, {
        headers: {
          'accept': '*/*',
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Login response:', response.data);
      
      // Check if the response indicates successful login
      if (!response.data || (response.data.error && response.data.error !== '')) {
        throw new Error(response.data?.error || 'Invalid username or password.');
      }
      
      console.log('Extracted user data:', {
        userId: response.data.userId,
        userName: response.data.userName,
        lastSelectedStation: response.data.lastSelectedStation,
        rtRights: response.data.rtRights
      });
      
      // Extract the token from the response (field names vary by environment)
      const rawToken =
        response.data.token ??
        response.data.Token ??
        response.data.accessToken ??
        response.data.access_token ??
        response.data.jwt;

      if (!rawToken) {
        throw new Error('No token received from login API');
      }

      const token = normalizeQuuBearerToken(String(rawToken));
      
      // Return the raw API response data for userData
      const user = response.data;
      
      return { user, token };
    } catch (error: any) {
      console.error('Login error:', error);
      
      // Handle different types of errors
      if (error.response?.status === 400) {
        const errorMessage = error.response.data?.message || error.response.data?.error || 'Invalid username or password.';
        throw new Error(errorMessage);
      } else if (error.response?.status === 401) {
        throw new Error('Invalid username or password.');
      } else if (error.response?.status === 500) {
        throw new Error('Server error. Please try again later.');
      } else if (error.code === 'NETWORK_ERROR' || error.code === 'ERR_NETWORK') {
        throw new Error('Network error. Please check your connection and try again.');
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Login failed. Please check your credentials and try again.');
      }
    }
  }

  async logout(): Promise<void> {
    return Promise.resolve();
  }

  async getCurrentUser(): Promise<User> {
    const userData = localStorage.getItem('userData');
    if (!userData) {
      throw new Error('No user data found');
    }
    return JSON.parse(userData);
  }

  async validateToken(token: string): Promise<User> {
    const t = normalizeQuuBearerToken(token);
    try {
      console.log('Validating token with GetUserDetails API...');
      const response = await this.quu.get(this.getUserDetailsURL, {
        headers: {
          'accept': '*/*',
          'Authorization': `Bearer ${t}`,
        },
        withCredentials: false,
      });
      
      console.log('GetUserDetails response:', response.data);
      
      if (!response.data || response.data.error) {
        throw new Error(response.data?.error || 'Token validation failed');
      }
      
      console.log('Extracted validation data:', {
        userId: response.data.userId,
        userName: response.data.userName,
        lastSelectedStation: response.data.lastSelectedStation,
        rtRights: response.data.rtRights
      });
      
      // Return the raw API response data
      return response.data;
    } catch (error: any) {
      console.error('Token validation error:', error);
      
      if (error.response?.status === 401) {
        this.handleSessionExpired();
      } else if (error.response?.status === 500) {
        throw new Error('Server error during token validation');
      } else if (error.code === 'NETWORK_ERROR' || error.code === 'ERR_NETWORK') {
        throw new Error('Network error during token validation');
      } else {
        throw new Error('Token validation failed');
      }
    }
  }

  async getUserStations(token: string): Promise<any[]> {
    const t = normalizeQuuBearerToken(token);
    const headers = {
      accept: '*/*',
      Authorization: `Bearer ${t}`,
    };

    try {
      console.log('Fetching user stations with token:', t.substring(0, 20) + '...');

      // Prefer Bearer-only (no cookies). Cross-site cookies with withCredentials
      // can cause 401s when a stale quu2go.com session conflicts with the JWT.
      let response;
      try {
        response = await this.quu.get(this.getUserStationsURL, {
          headers,
          withCredentials: false,
        });
      } catch (firstErr: any) {
        if (firstErr.response?.status === 401) {
          response = await this.quu.get(this.getUserStationsURL, {
            headers,
            withCredentials: true,
          });
        } else {
          throw firstErr;
        }
      }

      console.log('GetUserStations response:', response.data);

      if (!response.data || response.data.error) {
        throw new Error(response.data?.error || 'Failed to fetch user stations');
      }

      return response.data;
    } catch (error: any) {
      console.error('GetUserStations error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);

      // Never hard-redirect here — StationSelection shows Retry/Logout instead.
      if (error.response?.status === 401) {
        throw this.attachHttpStatus(
          new Error('Token is invalid or expired'),
          401,
          error.response
        );
      } else if (error.response?.status === 500) {
        throw new Error('Server error while fetching stations');
      } else if (error.code === 'NETWORK_ERROR' || error.code === 'ERR_NETWORK') {
        throw new Error('Network error while fetching stations');
      } else if (error.message) {
        throw this.attachHttpStatus(error, error.response?.status, error.response);
      } else {
        throw new Error('Failed to fetch user stations');
      }
    }
  }

  // Station
  async getStation(): Promise<Station> {
    const response = await this.api.get('/station');
    return response.data;
  }

  async getNowPlaying(stationId?: string): Promise<Station['nowPlaying']> {
    console.log('getNowPlaying called with stationId:', stationId);

    try {
      // Get station ID from localStorage if not provided
      const selectedStation = localStorage.getItem('selectedStation');
      console.log('Selected station from localStorage:', selectedStation);
      let currentStationId = stationId;
      
      if (!currentStationId && selectedStation) {
        try {
          const station = JSON.parse(selectedStation);
          currentStationId = station.id.toString();
          console.log('Using stationId from selectedStation:', currentStationId);
        } catch (error) {
          console.error('Error parsing selected station:', error);
        }
      }
      
      if (!currentStationId) {
        console.error('No station ID available');
        throw new Error('No station ID available');
      }
      
      console.log('Fetching now playing for station:', currentStationId);
      
      const tokenRaw = localStorage.getItem('authToken');
      if (!tokenRaw) {
        throw new Error('No authentication token available');
      }
      const token = normalizeQuuBearerToken(tokenRaw);
      
      // Call the GetNowPlaying API with station ID and HD parameters
      const response = await this.quu.get(
        `${this.getNowPlayingURL}?stationId=${currentStationId}&display=hd&hd=1`,
        {
          headers: {
            'accept': '*/*',
            'Authorization': `Bearer ${token}`,
          },
          withCredentials: false,
        }
      );
      
      console.log('GetNowPlaying response:', response.data);
      
      if (!response.data || response.data.error) {
        throw new Error(response.data?.error || 'Failed to fetch now playing data');
      }
      
      // Transform the API response to match our NowPlaying interface
      const nowPlaying: Station['nowPlaying'] = {
        title: response.data.title || response.data.songTitle || '',
        artist: response.data.artist || response.data.artistName || '',
        album: response.data.album || response.data.albumName || '',
        artwork: response.data.logoUrl || response.data.artwork || response.data.albumArt || response.data.imageUrl || ''
      };
      
      return nowPlaying;
    } catch (error: any) {
      console.error('GetNowPlaying error:', error);
      this.ensureAuthorized(error);
      if (error.response?.status === 500) {
        throw new Error('Server error while fetching now playing');
      } else if (error.code === 'NETWORK_ERROR' || error.code === 'ERR_NETWORK') {
        throw new Error('Network error while fetching now playing');
      } else {
        throw new Error('Failed to fetch now playing data');
      }
    }
  }

  // Categories — station-scoped list from HotQuusCategory API
  async getCategories(stationId?: string): Promise<Category[]> {
    const resolvedId = this.resolveStationId(stationId);

    try {
      const tokenRaw = localStorage.getItem('authToken');
      console.log('Fetching categories from HotQuusCategory/List for station:', resolvedId);
      const response = await this.quu.get(this.hotQuusCategoryListURL, {
        params: { stationId: resolvedId },
        headers: {
          accept: '*/*',
          ...(tokenRaw
            ? { Authorization: `Bearer ${normalizeQuuBearerToken(tokenRaw)}` }
            : {}),
        },
        withCredentials: false,
      });

      const raw = response.data;
      console.log('HotQuusCategory/List raw response:', raw);
      const apiCategories = this.extractHotQuusCategoryListItems(raw);
      console.log('Categories API Response count:', apiCategories.length);

      const transformedCategories = apiCategories
        .map((apiCategory: Record<string, unknown>) => {
          const name = String(
            this.pickField(
              apiCategory,
              'name',
              'Name',
              'categoryName',
              'CategoryName',
              'title',
              'Title'
            ) ?? ''
          ).trim();
          const idVal =
            this.pickField(
              apiCategory,
              'hotQuusId',
              'HotQuusId',
              'hot_quus_id',
              'hotQuusCategoryId',
              'HotQuusCategoryId',
              'categoryId',
              'CategoryId',
              'id',
              'Id'
            ) ?? name;
          const rawColor = this.pickField(
            apiCategory,
            'color',
            'Color',
            'bgColor',
            'backgroundColor'
          );
          const color = (() => {
            if (typeof rawColor !== 'string' || rawColor.trim() === '') {
              return this.getCategoryColor(name || 'Misc');
            }
            const t = rawColor.trim();
            if (t.startsWith('#') || /^rgb/i.test(t)) return t;
            return `#${t.replace(/^#/, '')}`;
          })();
          const pin = this.pickField(apiCategory, 'accessPin', 'AccessPin', 'access_pin');
          const isPrivate = Boolean(
            this.pickField(apiCategory, 'isPrivate', 'IsPrivate') ||
              (pin != null && String(pin).trim() !== '')
          );
          return {
            id: idVal != null && String(idVal).trim() !== '' ? String(idVal) : name || 'Misc',
            name: name || String(idVal ?? 'Misc'),
            color,
            isPrivate,
            isDefault: String(name).toLowerCase() === 'misc',
          };
        })
        .filter((c) => c.name.trim() !== '');

      console.log('Transformed categories:', transformedCategories);
      return transformedCategories;
    } catch (error: any) {
      console.error('Error fetching categories from HotQuusCategory/List:', error);
      this.ensureAuthorized(error);
      throw new Error('Failed to fetch categories');
    }
  }

  /** Normalize HotQuusCategory/List payload shapes. */
  private extractHotQuusCategoryListItems(raw: unknown): Record<string, unknown>[] {
    const parsed = this.coerceJsonValue(raw);
    const found = this.findHotQuusCategoryArray(parsed, 0);
    if (found.length) return found;

    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      console.warn(
        'HotQuusCategory/List: could not find item array. Top-level keys:',
        Object.keys(parsed as object),
        parsed
      );
    } else {
      console.warn('HotQuusCategory/List: unexpected payload shape:', parsed);
    }
    return [];
  }

  private findHotQuusCategoryArray(node: unknown, depth: number): Record<string, unknown>[] {
    if (node == null || depth > 6) return [];

    if (typeof node === 'string') {
      return this.findHotQuusCategoryArray(this.coerceJsonValue(node), depth + 1);
    }

    if (Array.isArray(node)) {
      if (!node.length) return [];
      const objectRows = node.filter(
        (x) => x && typeof x === 'object' && !Array.isArray(x)
      ) as Record<string, unknown>[];
      if (objectRows.length && objectRows.some((r) => this.looksLikeCategoryRow(r))) {
        return objectRows;
      }
      if (depth === 0 && objectRows.length) return objectRows;
      for (const child of node) {
        const found = this.findHotQuusCategoryArray(child, depth + 1);
        if (found.length) return found;
      }
      return [];
    }

    if (typeof node !== 'object') return [];

    const o = node as Record<string, unknown>;
    const preferredKeys = [
      'aaData',
      'categories',
      'Categories',
      'items',
      'Items',
      'rows',
      'Rows',
      'data',
      'Data',
      'list',
      'List',
      'records',
      'Records',
      'result',
      'Result',
      'results',
      'Results',
      'Table',
      'table',
      'values',
      'Values',
      'd',
    ];

    for (const key of preferredKeys) {
      if (!(key in o)) continue;
      const found = this.findHotQuusCategoryArray(o[key], depth + 1);
      if (found.length) return found;
    }

    for (const value of Object.values(o)) {
      const found = this.findHotQuusCategoryArray(value, depth + 1);
      if (found.length) return found;
    }
    return [];
  }

  private looksLikeCategoryRow(row: Record<string, unknown>): boolean {
    const lower = Object.keys(row).map((k) => k.toLowerCase().replace(/_/g, ''));
    const hasId = lower.some((k) =>
      ['hotquusid', 'hotquuscategoryid', 'categoryid', 'id'].includes(k)
    );
    const hasName = lower.some((k) => ['name', 'categoryname', 'title'].includes(k));
    const hasColor = lower.some((k) => ['color', 'bgcolor', 'backgroundcolor'].includes(k));
    return (hasId && hasName) || (hasName && hasColor) || hasName;
  }

  async getPrivateCategory(passcode: string): Promise<Category> {
    const response = await this.api.post('/categories/private', { passcode });
    return response.data;
  }

  // Hot Keys
  async getHotKeys(categoryId?: string, isActive?: boolean): Promise<HotKey[]> {
    // Backwards-compatible wrapper that uses the station from localStorage
    return this.getHotKeysForStation(undefined, categoryId, isActive);
  }

  async getHotKeysForStation(
    stationIdOverride?: string,
    categoryId?: string,
    isActive?: boolean
  ): Promise<HotKey[]> {
    try {
      const stationId = this.resolveStationId(stationIdOverride);
      console.log('Fetching hot keys from BlastHotQuus/List for station:', stationId);

      const tokenRaw = localStorage.getItem('authToken');
      if (!tokenRaw) {
        this.handleSessionExpired();
      }

      const response = await this.quu.get(this.blastHotQuusListURL, {
        params: {
          stationId,
          status: '1,2',
          sort: 'is_active,id',
          dir: 'desc',
          start: 0,
          limit: 50,
          name: '',
        },
        headers: {
          accept: '*/*',
          Authorization: `Bearer ${normalizeQuuBearerToken(tokenRaw)}`,
        },
        withCredentials: false,
      });

      const raw = response.data;
      console.log('BlastHotQuus/List raw response:', raw);
      const apiHotKeys = this.extractBlastHotQuuListItems(raw);
      console.log('BlastHotQuus/List response count:', apiHotKeys.length);

      // Dashboard shows only HotQuus with type = 1
      const typeOneHotKeys = apiHotKeys.filter((row) => {
        const t = this.pickField(row, 'type', 'Type');
        return t === 1 || t === '1' || Number(t) === 1;
      });
      console.log('BlastHotQuus/List type=1 count:', typeOneHotKeys.length);

      if (!typeOneHotKeys.length) {
        console.log('No HotQuus with type=1 for this station');
        return [];
      }

      let transformedHotKeys = typeOneHotKeys.map((apiHotKey: Record<string, unknown>) =>
        this.mapBlastHotQuuListItem(apiHotKey)
      );

      if (categoryId && categoryId !== 'all') {
        const cat = String(categoryId).toLowerCase();
        transformedHotKeys = transformedHotKeys.filter(
          (h) =>
            String(h.categoryId).toLowerCase() === cat ||
            String(h.category?.name ?? '').toLowerCase() === cat
        );
      }
      if (typeof isActive === 'boolean') {
        transformedHotKeys = transformedHotKeys.filter((h) => h.isActive === isActive);
      }

      console.log('Transformed hot keys:', transformedHotKeys);
      return transformedHotKeys;
    } catch (error: any) {
      console.error('Error fetching hot keys from BlastHotQuus/List:', error);
      this.ensureAuthorized(error);
      throw error;
    }
  }

  /** Normalize BlastHotQuus/List payload shapes (array / nested wrappers / DataTables). */
  private extractBlastHotQuuListItems(raw: unknown): Record<string, unknown>[] {
    const parsed = this.coerceJsonValue(raw);
    const found = this.findBlastHotQuuArray(parsed, 0);
    if (found.length) return found;

    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      console.warn(
        'BlastHotQuus/List: could not find item array. Top-level keys:',
        Object.keys(parsed as object),
        parsed
      );
    } else {
      console.warn('BlastHotQuus/List: unexpected payload shape:', parsed);
    }
    return [];
  }

  private coerceJsonValue(raw: unknown): unknown {
    if (typeof raw !== 'string') return raw;
    const trimmed = raw.trim();
    if (!trimmed) return raw;
    try {
      return JSON.parse(trimmed);
    } catch {
      return raw;
    }
  }

  /** Prefer arrays whose objects look like BlastHotQuu rows. */
  private findBlastHotQuuArray(node: unknown, depth: number): Record<string, unknown>[] {
    if (node == null || depth > 6) return [];

    if (typeof node === 'string') {
      return this.findBlastHotQuuArray(this.coerceJsonValue(node), depth + 1);
    }

    if (Array.isArray(node)) {
      if (!node.length) return [];
      if (node.every((x) => x && typeof x === 'object' && !Array.isArray(x))) {
        const rows = node as Record<string, unknown>[];
        if (rows.some((r) => this.looksLikeBlastHotQuuRow(r))) return rows;
        // Root-level object array with no recognizable fields — still use it
        if (depth === 0) return rows;
        return [];
      }
      // Rare: [meta, [...items]]
      for (const child of node) {
        const found = this.findBlastHotQuuArray(child, depth + 1);
        if (found.length) return found;
      }
      return [];
    }

    if (typeof node !== 'object') return [];

    const o = node as Record<string, unknown>;
    const preferredKeys = [
      'aaData',
      'data',
      'Data',
      'items',
      'Items',
      'rows',
      'Rows',
      'results',
      'Results',
      'result',
      'Result',
      'list',
      'List',
      'records',
      'Records',
      'blasts',
      'Blasts',
      'hotQuus',
      'HotQuus',
      'blastHotQuus',
      'BlastHotQuus',
      'values',
      'Values',
      'd',
    ];

    for (const key of preferredKeys) {
      if (key in o) {
        const found = this.findBlastHotQuuArray(o[key], depth + 1);
        if (found.length) return found;
      }
    }

    // Fallback: scan all object values
    for (const value of Object.values(o)) {
      const found = this.findBlastHotQuuArray(value, depth + 1);
      if (found.length) return found;
    }
    return [];
  }

  private looksLikeBlastHotQuuRow(row: Record<string, unknown>): boolean {
    const keys = Object.keys(row);
    const lower = keys.map((k) => k.toLowerCase().replace(/_/g, ''));
    const hasId = lower.some((k) =>
      ['blastshotquuid', 'id', 'hotquuid', 'messageid'].includes(k)
    );
    const hasName = lower.some((k) => ['name', 'title', 'hotquuname'].includes(k));
    const hasLine = lower.some((k) =>
      ['quuline1', 'rt1', 'line1', 'messagetext', 'text'].includes(k)
    );
    const hasActive = lower.some((k) =>
      ['isactive', 'active', 'activate', 'status'].includes(k)
    );
    return (hasId && hasName) || (hasName && hasLine) || (hasId && hasActive) || hasId;
  }

  private pickField(row: Record<string, unknown>, ...names: string[]): unknown {
    const entries = Object.entries(row);
    for (const name of names) {
      if (name in row && row[name] != null && row[name] !== '') return row[name];
      const want = name.toLowerCase().replace(/_/g, '');
      for (const [k, v] of entries) {
        if (k.toLowerCase().replace(/_/g, '') === want && v != null && v !== '') return v;
      }
    }
    return undefined;
  }

  private mapBlastHotQuuListItem(apiHotKey: Record<string, unknown>): HotKey {
    const idVal = this.pickField(
      apiHotKey,
      'blastsHotQuuId',
      'BlastsHotQuuId',
      'blasts_hot_quu_id',
      'id',
      'Id',
      'hotQuuId'
    );
    const name = String(this.pickField(apiHotKey, 'name', 'Name', 'title', 'Title') ?? '');
    const line1 = String(
      this.pickField(apiHotKey, 'quuLine1', 'QuuLine1', 'quu_line_1', 'rt1', 'line1') ?? ''
    );
    const line2 = String(
      this.pickField(apiHotKey, 'quuLine2', 'QuuLine2', 'quu_line_2', 'rt2', 'line2') ?? ''
    );
    const messageText = line2 ? `${line1} - ${line2}` : line1;

    // BlastHotQuus: Type is often always 2; Rotation holds takeover/rotate/override.
    const rotationOrType = this.pickField(
      apiHotKey,
      'rotation',
      'Rotation',
      'type',
      'Type'
    );
    const opts = this.normalizeDisplayDuringOptionIds(apiHotKey);

    const categoryRaw =
      this.pickField(
        apiHotKey,
        'categoryName',
        'CategoryName',
        'category_name',
        'category',
        'Category'
      ) ?? 'Misc';
    const categoryName =
      typeof categoryRaw === 'object' && categoryRaw != null
        ? String(
            (categoryRaw as Record<string, unknown>).name ??
              (categoryRaw as Record<string, unknown>).Name ??
              'Misc'
          )
        : String(categoryRaw);
    const categoryIdRaw =
      this.pickField(apiHotKey, 'categoryId', 'CategoryId', 'category_id') ??
      (typeof categoryRaw === 'number' || /^\d+$/.test(String(categoryRaw))
        ? categoryRaw
        : categoryName);
    const categoryId = String(categoryIdRaw ?? categoryName);

    const rawTileColor = this.pickField(
      apiHotKey,
      'color',
      'Color',
      'tileColor',
      'cardColor',
      'bgColor'
    );
    const tileColor =
      typeof rawTileColor === 'string' && rawTileColor.trim() !== ''
        ? (() => {
            const t = rawTileColor.trim();
            if (t.startsWith('#') || /^rgb/i.test(t)) return t;
            return `#${t.replace(/^#/, '')}`;
          })()
        : undefined;

    const logoValue = this.pickField(
      apiHotKey,
      'image',
      'Image',
      'imageUrl',
      'ImageUrl',
      'imageURL',
      'image_url',
      'logo',
      'imageName'
    );
    const idStr = idVal != null ? String(idVal) : '';

    const activeRaw = this.pickField(
      apiHotKey,
      'isActive',
      'IsActive',
      'is_active',
      'activate',
      'active',
      'Active',
      'status',
      'Status'
    );

    return {
      id: idStr,
      name,
      messageText,
      rt1: line1,
      rt2: line2,
      tileColor,
      messageImage: logoValue
        ? resolveMessageImageUrl(idStr, String(logoValue))
        : undefined,
      type: this.mapApiTypeToMessageType(rotationOrType as string | number),
      categoryId,
      category: {
        id: categoryId,
        name: categoryName,
        color: this.getCategoryColor(categoryName),
        isPrivate: false,
        isDefault: categoryName === 'Misc',
      },
      isActive: this.mapBlastIsActiveToOnAir(activeRaw),
      keepAliveMinutes: Number(
        this.pickField(apiHotKey, 'keepAlive', 'KeepAlive', 'keep_alive', 'minutesAlive')
      ) || 90,
      rotationDurationSeconds:
        Number(
          this.pickField(
            apiHotKey,
            'duration',
            'Duration',
            'durationInSeconds',
            'duration_in_seconds'
          )
        ) || 30,
      duringSettings: {
        music: opts.includes('1'),
        other: opts.includes('2'),
        ads: opts.includes('3'),
      },
      createdAt: new Date(),
      updatedAt: new Date(),
    };
  }

  /**
   * BlastHotQuus status:
   * - isActive 1 = On Air
   * - isActive 2 = Inactive
   */
  private mapBlastIsActiveToOnAir(raw: unknown): boolean {
    if (raw === true) return true;
    if (raw === false) return false;
    const n = Number(raw);
    if (n === 1) return true;
    if (n === 2) return false;
    const s = String(raw ?? '').trim().toLowerCase();
    if (s === '1' || s === 'true' || s === 'onair' || s === 'on air' || s === 'active') return true;
    if (s === '2' || s === 'false' || s === 'inactive') return false;
    return false;
  }

  /** Map UI on-air flag to BlastHotQuus IsActive value (1 = on air, 2 = inactive). */
  private blastIsActiveApiValue(onAir: boolean): 1 | 2 {
    return onAir ? 1 : 2;
  }

  /** Quu emergency blast / BlastHotQuus Rotation: 0 = Takeover, 1 = Rotate, 2 = Override. */
  private mapApiTypeToMessageType(apiType: string | number): MessageType {
    const t = String(apiType ?? '').trim().toLowerCase();
    if (t === '0' || t === 'takeover' || t === 'take over') return MessageType.TAKEOVER;
    if (t === '1' || t === 'rotate' || t === 'rotate_with' || t === 'rotate with' || t === 'rotate with billboards')
      return MessageType.ROTATE_WITH;
    if (t === '2' || t === 'override' || t === 'override billboards' || t === 'override billboard')
      return MessageType.OVERRIDE;
    return MessageType.ROTATE_WITH;
  }

  private buildImageUrl(messageId: string, logo: string): string {
    return resolveMessageImageUrl(messageId, logo);
  }

  /**
   * Display During option_ids: 1 = Music, 2 = Other, 3 = Ads.
   * Reads `options`, `option_ids`, or BlastHotQuus `DisplayDuring` ("1,2,3").
   */
  private normalizeDisplayDuringOptionIds(apiHotKey: Record<string, unknown>): string[] {
    const displayDuring = apiHotKey.displayDuring ?? apiHotKey.DisplayDuring;
    if (typeof displayDuring === 'string' && displayDuring.trim()) {
      return displayDuring
        .split(/[,|;:\s]+/)
        .map((x) => x.trim())
        .filter(Boolean);
    }
    if (Array.isArray(displayDuring)) {
      return displayDuring.map((x) => String(x).trim());
    }
    const raw = apiHotKey.options ?? apiHotKey.option_ids;
    if (!Array.isArray(raw)) return [];
    return raw.map((x: unknown) => String(x).trim());
  }

  private getCategoryColor(category: string): string {
    switch (category.toLowerCase()) {
      case 'misc':
        return '#1976d2';
      case 'news':
        return '#ff9800';
      case 'sports':
        return '#2e7d32';
      case 'weather':
        return '#2196f3';
      case 'emergency':
        return '#d32f2f';
      case 'promotion':
        return '#2e7d32';
      default:
        return DEFAULT_HOTQUU_TILE_COLOR;
    }
  }

  async getHotKey(id: string): Promise<HotKey> {
    const response = await this.api.get(`/hotkeys/${id}`);
    return response.data;
  }

  async createHotKey(hotKey: Partial<HotKey>): Promise<HotKey> {
    const response = await this.api.post('/hotkeys', hotKey);
    return response.data;
  }

  async updateHotKey(id: string, updates: Partial<HotKey>): Promise<HotKey> {
    const response = await this.api.put(`/hotkeys/${id}`, updates);
    return response.data;
  }

  /**
   * Upload a HotQuu image to our API server, then return an absolute public URL
   * to send as `imageUrl` on the Quu save request (#45).
   */
  async uploadHotQuuImage(file: File): Promise<string> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('image', file);

    const uploadPath = (
      process.env.REACT_APP_IMAGE_UPLOAD_PATH || '/images/upload'
    ).replace(/^\//, '');

    try {
      const response = await this.api.post(`/${uploadPath}`.replace(/\/{2,}/g, '/'), formData);

      const data = response.data;
      const raw =
        (typeof data === 'string' && data) ||
        data?.url ||
        data?.imageUrl ||
        data?.imageURL ||
        data?.publicUrl ||
        data?.path ||
        data?.fileUrl ||
        data?.location ||
        data?.data?.url ||
        data?.data?.imageUrl;

      if (!raw || typeof raw !== 'string' || !raw.trim()) {
        throw new Error('Image upload succeeded but no image URL was returned by the server.');
      }

      return this.toAbsoluteImageUrl(raw.trim());
    } catch (error: any) {
      if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        throw new Error(
          'Could not reach the image upload service. Please try again, or save without an image.'
        );
      }
      if (error.message && !error.response) throw error;
      const d = error.response?.data;
      const detail =
        typeof d === 'string'
          ? d
          : d?.message || d?.error || d?.Message || error.message;
      throw new Error(detail || 'Failed to upload image to server.');
    }
  }

  /** Turn a relative API path into a full URL for Quu / browsers. */
  private toAbsoluteImageUrl(urlOrPath: string): string {
    if (/^https?:\/\//i.test(urlOrPath)) return urlOrPath;
    if (urlOrPath.startsWith('//')) return `https:${urlOrPath}`;

    const publicBase = (
      process.env.REACT_APP_API_PUBLIC_URL ||
      (typeof window !== 'undefined' && window.location.protocol === 'https:'
        ? window.location.origin
        : this.baseURL.replace(/\/api\/?$/i, ''))
    ).replace(/\/+$/, '');

    const path = urlOrPath.startsWith('/') ? urlOrPath : `/${urlOrPath}`;
    return `${publicBase}${path}`;
  }

  /** DisplayDuring for BlastHotQuus/Save — comma-separated option ids (1 Music, 2 Other, 3 Ads). */
  private buildDisplayDuringValue(optionIds: number[]): string {
    const ids = optionIds.filter((n) => !Number.isNaN(n) && n > 0);
    return ids.length ? ids.join(',') : '1,2,3';
  }

  private resolveBlastHotQuuCategoryId(payload: {
    category?: string;
    categoryId?: string | number;
  }): number | undefined {
    if (payload.categoryId != null && String(payload.categoryId).trim() !== '') {
      const n = Number(payload.categoryId);
      if (Number.isFinite(n)) return n;
    }
    if (payload.category != null && /^\d+$/.test(String(payload.category).trim())) {
      return Number(payload.category);
    }
    return undefined;
  }

  /**
   * Resolve BlastHotQuus ImageFile (binary). Prefer an explicit File/Blob; otherwise
   * fetch a public http(s) URL and wrap it as a Blob for multipart upload.
   */
  private async resolveImageFileBinary(
    imageFile?: File | Blob | null,
    imageUrl?: string | null
  ): Promise<{ blob: Blob; filename: string } | null> {
    if (imageFile) {
      const filename =
        imageFile instanceof File && imageFile.name
          ? imageFile.name
          : `hotquu-image.${(imageFile.type || 'image/jpeg').split('/')[1] || 'jpg'}`;
      return { blob: imageFile, filename };
    }

    const raw = String(imageUrl ?? '').trim();
    if (!raw || raw.startsWith('data:') || !/^https?:\/\//i.test(raw)) {
      return null;
    }

    try {
      const res = await fetch(raw);
      if (!res.ok) return null;
      const blob = await res.blob();
      if (!blob.size) return null;
      const pathName = (() => {
        try {
          return decodeURIComponent(new URL(raw).pathname.split('/').pop() || '');
        } catch {
          return '';
        }
      })();
      const filename =
        pathName && /\.(png|jpe?g|gif|webp|svg)$/i.test(pathName)
          ? pathName
          : `hotquu-image.${(blob.type || 'image/jpeg').split('/')[1] || 'jpg'}`;
      return { blob, filename };
    } catch {
      return null;
    }
  }

  /**
   * Create or update a HotQuu via Quu2Go BlastHotQuus/Save.
   * Pass id: null (or omit) for create; pass existing id for update.
   * Image is sent as multipart binary field `ImageFile` (Swagger string($binary)).
   */
  async saveHotKey(payload: {
    id?: string | null;
    name: string;
    category: string;
    categoryId?: string | number;
    minutesAlive: number;
    rt1: string;
    rt2?: string;
    /** Binary image for ImageFile (preferred). */
    imageFile?: File | Blob | null;
    /** Optional http(s) URL used only when imageFile is not provided (e.g. Newsflow). */
    imageUrl?: string | null;
    radio_station_ids: number[];
    /** Maps to BlastHotQuus Rotation: 0 = takeover, 1 = rotate, 2 = override */
    type?: string | number;
    durationInSeconds?: number;
    /** Prefer numeric ids; else `options` strings '1'|'2'|'3' are converted */
    option_ids?: number[];
    options?: string[];
  }, activate: 0 | 1 = 0): Promise<{ id: number; name: string; schedules: unknown; active: number }> {
    try {
      const selectedStation = localStorage.getItem('selectedStation');
      let stationIds = payload.radio_station_ids;
      if (!stationIds.length && selectedStation) {
        try {
          const station = JSON.parse(selectedStation);
          stationIds = [Number(station.id)];
        } catch {
          stationIds = [];
        }
      }

      const rotationNum =
        payload.type !== undefined && payload.type !== null && String(payload.type) !== ''
          ? Number(payload.type)
          : 1;
      const duration = Number(payload.durationInSeconds ?? 30);
      let optionIds: number[] = [];
      if (payload.option_ids != null && payload.option_ids.length > 0) {
        optionIds = payload.option_ids.map((n) => Number(n)).filter((n) => !Number.isNaN(n));
      } else if (payload.options != null) {
        optionIds = payload.options
          .map((x) => Number(x))
          .filter((n) => !Number.isNaN(n));
      }

      const parsedBlastId =
        payload.id != null && String(payload.id).trim() !== ''
          ? Number(String(payload.id).trim())
          : NaN;
      const blastNumericId = Number.isFinite(parsedBlastId) ? parsedBlastId : null;

      const formData = new FormData();
      if (blastNumericId != null) {
        formData.append('BlastsHotQuuId', String(blastNumericId));
      }
      formData.append('Name', payload.name);
      formData.append('Type', String(ApiService.BLAST_HOTQUU_TYPE));
      formData.append('Rotation', String(Number.isNaN(rotationNum) ? 1 : rotationNum));
      formData.append('Duration', String(Math.max(1, Number.isNaN(duration) ? 30 : duration)));
      formData.append('KeepAlive', String(Math.max(1, payload.minutesAlive || 90)));
      formData.append('DisplayDuring', this.buildDisplayDuringValue(optionIds));
      formData.append('QuuLine1', payload.rt1);
      formData.append('QuuLine2', payload.rt2 ?? '');

      const categoryId = this.resolveBlastHotQuuCategoryId(payload);
      if (categoryId != null) {
        formData.append('Category', String(categoryId));
      }

      const imageBinary = await this.resolveImageFileBinary(payload.imageFile, payload.imageUrl);
      if (imageBinary) {
        formData.append('ImageFile', imageBinary.blob, imageBinary.filename);
      }

      stationIds
        .filter((id) => Number.isFinite(id))
        .forEach((id) => formData.append('Stations', String(id)));

      formData.append('IsActive', String(this.blastIsActiveApiValue(activate === 1)));

      const tokenRaw = localStorage.getItem('authToken');
      if (!tokenRaw) {
        this.handleSessionExpired();
      }
      const response = await this.quu.post(this.blastHotQuusSaveURL, formData, {
        headers: {
          Authorization: `Bearer ${normalizeQuuBearerToken(tokenRaw!)}`,
        },
        withCredentials: false,
      });

      const result = response.data;
      if (Array.isArray(result) && result[0]) {
        return this.normalizeBlastHotQuuSaveResult(result[0], payload.name, activate);
      }
      return this.normalizeBlastHotQuuSaveResult(result, payload.name, activate);
    } catch (error: any) {
      this.ensureAuthorized(error);
      if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        throw new Error(
          'Could not reach the HotQuu save API. Check your connection and try again.'
        );
      }
      const d = error.response?.data;
      const detail =
        typeof d === 'string'
          ? d
          : d?.message || d?.error || d?.Message || JSON.stringify(d);
      throw new Error(detail || error.message || 'Failed to save message');
    }
  }

  private normalizeBlastHotQuuSaveResult(
    result: unknown,
    fallbackName: string,
    activate: 0 | 1
  ): { id: number; name: string; schedules: unknown; active: number } {
    const r = (result ?? {}) as Record<string, unknown>;
    const rawId =
      r.blastsHotQuuId ??
      r.BlastsHotQuuId ??
      r.id ??
      r.Id ??
      r.hotQuuId ??
      r.HotQuuId;
    const id = Number(rawId);
    const name = String(r.name ?? r.Name ?? fallbackName);
    const activeRaw = r.isActive ?? r.IsActive ?? r.active ?? r.Active ?? (activate === 1 ? 1 : 2);
    const onAir = this.mapBlastIsActiveToOnAir(activeRaw);
    return {
      id: Number.isFinite(id) ? id : 0,
      name,
      schedules: r.schedules ?? r.Schedules ?? null,
      active: this.blastIsActiveApiValue(onAir),
    };
  }

  /**
   * Ingest a Quu Dashboard / Newsflow card into HotQuus using station-level settings (#40).
   * Manual → activate 0 (queued Ready); Automatic → activate 1 (publish on air).
   */
  async ingestNewsflowCard(card: {
    name?: string;
    title?: string;
    line1?: string;
    line2?: string;
    rt1?: string;
    rt2?: string;
    imageUrl?: string | null;
    stationId: string | number;
  }): Promise<{ id: number; name: string; schedules: unknown; active: number }> {
    const { buildHotKeyPayloadFromNewsflow, loadNewsflowStationSettings } = await import(
      '../utils/newsflowStationSettings'
    );
    const stationId = String(card.stationId);
    const settings = loadNewsflowStationSettings(stationId);
    const built = buildHotKeyPayloadFromNewsflow(card, settings);
    const { activate, ...payload } = built;
    return this.saveHotKey(payload, activate);
  }

  async deleteHotKey(id: string): Promise<void> {
    await this.api.delete(`/hotkeys/${id}`);
  }

  // Preview and Send — BlastHotQuus/Update (IsActive: 1 = on air, 2 = inactive)
  async startHotKey(id: string, _keepAliveMinutes?: number): Promise<void> {
    try {
      console.log(`Activating HotQuu via BlastHotQuus/Update, id: ${id}`);
      await this.updateBlastHotQuuStatus(id, true);
    } catch (error: any) {
      this.ensureAuthorized(error);
      console.error('Error starting hot key:', error);
      console.error('Response:', error.response?.status, error.response?.data);
      const msg = this.getErrorMessage(error, 'Failed to start hot key');
      throw new Error(msg);
    }
  }

  async stopHotKey(id: string): Promise<void> {
    try {
      console.log(`Deactivating HotQuu via BlastHotQuus/Update, id: ${id}`);
      await this.updateBlastHotQuuStatus(id, false);
    } catch (error: any) {
      this.ensureAuthorized(error);
      const msg = this.getErrorMessage(error, 'Failed to deactivate hot key');
      throw new Error(msg);
    }
  }

  /**
   * Activate (IsActive=1) or deactivate (IsActive=2) via BlastHotQuus/Update.
   */
  private async updateBlastHotQuuStatus(id: string, onAir: boolean): Promise<void> {
    const blastId = Number(String(id).trim());
    if (!Number.isFinite(blastId)) {
      throw new Error('Invalid HotQuu id');
    }

    const formData = new FormData();
    formData.append('BlastsHotQuuId', String(blastId));
    formData.append('Type', String(ApiService.BLAST_HOTQUU_TYPE));
    formData.append('IsActive', String(this.blastIsActiveApiValue(onAir)));

    const userId = this.resolveCurrentUserId();
    if (userId != null) {
      formData.append('UserId', String(userId));
    }

    try {
      const tokenRaw = localStorage.getItem('authToken');
      if (!tokenRaw) {
        this.handleSessionExpired();
      }
      const response = await this.quu.post(this.blastHotQuusUpdateURL, formData, {
        headers: {
          Authorization: `Bearer ${normalizeQuuBearerToken(tokenRaw!)}`,
        },
        withCredentials: false,
      });
      console.log('BlastHotQuus/Update response:', response.data);
    } catch (error: any) {
      this.ensureAuthorized(error);
      throw error;
    }
  }

  private resolveCurrentUserId(): number | undefined {
    try {
      const raw = localStorage.getItem('userData');
      if (!raw) return undefined;
      const user = JSON.parse(raw) as Record<string, unknown>;
      const id = user.userId ?? user.UserId ?? user.id ?? user.Id;
      const n = Number(id);
      return Number.isFinite(n) ? n : undefined;
    } catch {
      return undefined;
    }
  }

  private getErrorMessage(error: any, fallback: string): string {
    if (!error) return fallback;
    if (error.message && error.message !== 'Network Error') return error.message;
    const d = error.response?.data;
    if (d && typeof d === 'string') return d;
    if (d?.message) return d.message;
    if (d?.error) return typeof d.error === 'string' ? d.error : d.error?.message || fallback;
    if (d?.Message) return d.Message;
    const status = error.response?.status;
    if (status === 403) return 'You do not have permission to perform this action.';
    if (status === 404) return 'Resource not found.';
    if (status && status >= 500) return 'Server error. Please try again later.';
    if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
      return 'Network error. Check the server is reachable and CORS is allowed.';
    }
    return fallback;
  }

  async getHotKeyStatus(id: string): Promise<{ isActive: boolean; timeRemaining?: number }> {
    const response = await this.api.get(`/hotkeys/${id}/status`);
    return response.data;
  }
}

export const apiService = new ApiService();
export default apiService; 